import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Plus, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function PromoCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "",
    percent_off: 10,
    description: "",
    active: true,
    expires_at: "",
    max_uses: "",
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/promo-codes");
      setCodes(r.data);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.code.trim() || !form.percent_off) {
      toast.error("Code and percent off are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        percent_off: Number(form.percent_off),
        description: form.description,
        active: !!form.active,
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      };
      await api.post("/admin/promo-codes", payload);
      toast.success(`Created ${payload.code}`);
      setShowForm(false);
      setForm({ code: "", percent_off: 10, description: "", active: true, expires_at: "", max_uses: "" });
      load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to create promo code");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;
    try {
      await api.delete(`/admin/promo-codes/${code}`);
      setCodes((prev) => prev.filter((c) => c.code !== code));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const copyCode = (code) => {
    try {
      navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch (e) {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-cream-200 p-12 text-center">
        <Loader2 className="w-5 h-5 animate-spin inline text-saffron" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="promo-codes-panel">
      <div className="bg-white border border-cream-200">
        <div className="flex items-center justify-between p-4 border-b border-cream-200">
          <div>
            <div className="text-sm font-medium">Promo codes</div>
            <div className="text-xs text-foreground/60">
              {codes.length} code{codes.length === 1 ? "" : "s"} · validated at checkout
            </div>
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            data-testid="toggle-promo-form"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron hover:bg-deepred text-white rounded-sm text-sm font-medium uppercase tracking-wider transition-colors"
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Close" : "New promo code"}
          </button>
        </div>

        {showForm && (
          <div className="p-5 border-b border-cream-200 bg-cream-50 space-y-4" data-testid="promo-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="p-code">Code *</Label>
                <Input
                  id="p-code"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="DIWALI10"
                  data-testid="promo-code-input"
                />
              </div>
              <div>
                <Label htmlFor="p-pct">Percent off (%) *</Label>
                <Input
                  id="p-pct"
                  type="number"
                  min="1"
                  max="90"
                  value={form.percent_off}
                  onChange={(e) => setForm({ ...form, percent_off: e.target.value })}
                  data-testid="promo-percent-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="p-desc">Description</Label>
              <Input
                id="p-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Diwali festival offer — Oct/Nov 2026"
                data-testid="promo-desc-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="p-exp">Expires at (optional)</Label>
                <Input
                  id="p-exp"
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  data-testid="promo-expires-input"
                />
              </div>
              <div>
                <Label htmlFor="p-max">Max uses (optional)</Label>
                <Input
                  id="p-max"
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  data-testid="promo-max-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="p-active"
                checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="promo-active-switch"
              />
              <Label htmlFor="p-active" className="text-sm cursor-pointer">
                Active (immediately available at checkout)
              </Label>
            </div>
            <div className="flex justify-end">
              <button
                onClick={create}
                disabled={saving}
                data-testid="create-promo-submit"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-saffron hover:bg-deepred text-white rounded-sm text-sm font-medium uppercase tracking-wider transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create
              </button>
            </div>
          </div>
        )}

        {codes.length === 0 ? (
          <div className="p-12 text-center text-foreground/60">
            No promo codes yet. Create your first one above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-cream-200">
              <tr className="text-left text-xs uppercase tracking-wider text-foreground/60">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">% off</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} className="border-b border-cream-100 hover:bg-cream-50" data-testid={`promo-row-${c.code}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-saffron">{c.code}</td>
                  <td className="px-4 py-3">{c.percent_off}%</td>
                  <td className="px-4 py-3 text-foreground/70">{c.description || "—"}</td>
                  <td className="px-4 py-3">
                    {c.uses || 0}
                    {c.max_uses ? <span className="text-foreground/40"> / {c.max_uses}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-foreground/70">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
                  <td className="px-4 py-3">
                    {c.active ? (
                      <span className="px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 text-xs font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-sm bg-gray-200 text-gray-700 text-xs">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => copyCode(c.code)}
                        className="text-foreground/50 hover:text-saffron"
                        title="Copy code"
                        data-testid={`copy-promo-${c.code}`}
                      >
                        {copied === c.code ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => remove(c.code)}
                        className="text-foreground/40 hover:text-destructive"
                        title="Delete"
                        data-testid={`delete-promo-${c.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
